import { useEffect, useState } from "react";
import { MessageSquare, ShieldCheck, X } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

const NOTICE_TEXT = "La société SARL AU KIAAD collecte et traite les messages échangés dans Hana en vue du suivi et de la mise en relation des patients avec les professionnels de santé référents. Les messages peuvent contenir des données personnelles et de santé. Ce traitement fait l'objet d'une autorisation préalable de la CNDP n° A-S-1164/2026. Pour exercer vos droits d'accès, de rectification et d'opposition conformément aux dispositions de la loi 09-08, vous pouvez contacter SARL AU KIAAD à l'adresse contact@kiaad.ma ou au 0716204810.";

export default function MessagingNoticeModal() {
  const [visible, setVisible] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/consents/messaging-notice/status", { skipAuthRedirect: true })
      .then((res) => {
        if (!mounted) return;
        setVisible(!res.data?.accepted);
      })
      .catch(() => {
        if (mounted) setVisible(false);
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const submit = async () => {
    if (!accepted || saving) return;
    setSaving(true);
    try {
      await api.post("/consents/messaging-notice/accept");
      toast.success("Mention messagerie enregistrée");
      setVisible(false);
    } catch {
      toast.error("Impossible d'enregistrer la mention messagerie");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !visible) return null;

  return (
    <div className="fixed inset-0 z-20 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 pb-28" data-testid="messaging-notice-modal">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden border border-teal-100">
        <div className="p-5 bg-gradient-to-r from-teal-700 to-emerald-700 text-white flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-extrabold">Information sur la messagerie Hana</h2>
            <p className="text-sm text-teal-50 mt-1">À lire lors de la première ouverture.</p>
          </div>
          <button data-testid="messaging-notice-close-disabled" className="opacity-40 cursor-not-allowed" aria-label="Fermeture désactivée tant que la mention n'est pas acceptée">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-2xl border border-teal-100 bg-teal-50/60 p-4 text-sm leading-relaxed text-slate-700" data-testid="messaging-notice-text">
            {NOTICE_TEXT}
          </div>
          <label className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-200 cursor-pointer" data-testid="messaging-notice-checkbox-label">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(event) => setAccepted(event.target.checked)}
              data-testid="messaging-notice-checkbox"
              className="mt-1 w-5 h-5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            />
            <span className="text-sm text-slate-700">
              J’ai lu et compris cette information relative au traitement des messages dans Hana.
            </span>
          </label>
        </div>
        <div className="p-5 bg-slate-50 border-t border-slate-100">
          <button
            onClick={submit}
            disabled={!accepted || saving}
            data-testid="messaging-notice-submit"
            className={`w-full py-3 rounded-2xl font-extrabold flex items-center justify-center gap-2 transition-colors ${accepted ? "bg-teal-700 text-white hover:bg-teal-800" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}
          >
            <ShieldCheck className="w-5 h-5" />
            {saving ? "Enregistrement..." : "J’ai compris et je continue"}
          </button>
        </div>
      </div>
    </div>
  );
}